import React, { useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useGetUserProfileQuery } from '../../redux/complaintApi';
import { useUpdateProfileImageMutation } from '../../redux/employeeApi';
import { accountImageDisplayUrl } from '../../utils/accountImageUrl';
import {
  accountableDepartmentLabelFromAccount,
  departmentNameFromAccount,
  designationNameFromAccount,
  locationNamesFromAccount,
  roleNameFromAccount,
} from '../../utils/accountRoleClassification';
import { getProbationFromAccount } from '../../utils/accountProbation';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { IMAGE_MAX_KB, validateImageFilesUnderSize } from '../../utils/validation';
import ProbationInfoFields from './ProbationInfoFields';

function formatDate(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatStatus(status) {
  if (status === true) return 'Active';
  if (status === false) return 'Inactive';
  return '—';
}

function isPdfUrl(url) {
  if (!url) return false;
  const noQuery = String(url).split('?')[0];
  const last = noQuery.split('/').filter(Boolean).pop() || '';
  try {
    return decodeURIComponent(last).toLowerCase().endsWith('.pdf');
  } catch {
    return last.toLowerCase().endsWith('.pdf');
  }
}

function fileNameFromUrl(url, fallback = 'document') {
  if (!url) return fallback;
  const noQuery = String(url).split('?')[0];
  const last = noQuery.split('/').filter(Boolean).pop() || fallback;
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}

function mapProfile(data) {
  if (!data || typeof data !== 'object') return null;
  return {
    employeeId: data.employee_id || '',
    name: data.name || '',
    email: data.email || '',
    contact: data.contact || '',
    department:
      accountableDepartmentLabelFromAccount(data) || departmentNameFromAccount(data) || '',
    designation: designationNameFromAccount(data) || '',
    roleName: roleNameFromAccount(data) || '',
    location: locationNamesFromAccount(data),
    manager: data.reporting_supervisor_name || '',
    dob: data.dob || '',
    doj: data.date_of_joining || '',
    aadharNo: data.aadhar_no || '',
    panNo: data.pan_number || '',
    status: data.status,
    employeeImage: accountImageDisplayUrl(data, 'employee_image'),
    aadharImage: accountImageDisplayUrl(data, 'aadhar_front_image'),
    panImage: accountImageDisplayUrl(data, 'pan_image'),
    ...getProbationFromAccount(data),
  };
}

function ProfileField({ label, value, wide = false }) {
  return (
    <div
      className={`rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 ${
        wide ? 'sm:col-span-2' : ''
      }`}
    >
      <p className="text-xs text-gray-500">{label}</p>
      <p
        className={`mt-0.5 text-sm font-medium text-gray-900 ${
          wide ? 'break-all' : ''
        }`}
      >
        {value || '—'}
      </p>
    </div>
  );
}

function UserProfilePage() {
  const { user } = useAuth();
  const employeeId = user?.employeeCode;
  const accountId = user?.id ?? data?.data?.id ?? data?.id;
  const fileInputRef = useRef(null);
  const { data, isFetching, isError, error, refetch } = useGetUserProfileQuery(employeeId, {
    skip: !employeeId,
  });
  const [updateProfileImage, { isLoading: isUploadingPhoto }] =
    useUpdateProfileImageMutation();
  const [zoomImage, setZoomImage] = useState(null);

  const profile = useMemo(() => mapProfile(data?.data ?? data), [data]);

  const displayName = profile?.name || user?.name || '—';
  const displayRole = profile?.roleName || user?.roleName || '—';
  const employeeImageSrc = profile?.employeeImage
    ? resolveAssetUrl(profile.employeeImage) || profile.employeeImage
    : '';
  const avatarSrc =
    employeeImageSrc && !isPdfUrl(employeeImageSrc) ? employeeImageSrc : '';

  const handleProfilePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    if (accountId == null || accountId === '') {
      toast.error('Unable to update profile photo. Please login again.');
      return;
    }
    const sizeError = validateImageFilesUnderSize([file]);
    if (sizeError) {
      toast.error(sizeError);
      return;
    }
    try {
      await updateProfileImage({
        id: accountId,
        employee_image: file,
      }).unwrap();
      toast.success('Profile photo updated successfully.');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update profile photo.');
    }
  };

  return (
    <DashboardLayout title="My Profile">
      <div className="space-y-4 py-4">
        {isError ? (
          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <p className="text-center text-sm text-red-600">
              {error?.data?.message || 'Unable to load profile. Please try again.'}
            </p>
          </div>
        ) : null}

        {!isError ? (
          <>
            <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
              <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start sm:gap-6">
                <div className="relative shrink-0">
                  <button
                    type="button"
                    className="h-28 w-28 overflow-hidden rounded-full bg-gray-100 ring-2 ring-[#F7E5D5] sm:h-32 sm:w-32"
                    onClick={() =>
                      avatarSrc
                        ? setZoomImage({
                            src: avatarSrc,
                            title: displayName,
                          })
                        : fileInputRef.current?.click()
                    }
                    aria-label={avatarSrc ? 'Preview employee photo' : 'Upload profile photo'}
                  >
                    {avatarSrc ? (
                      <img
                        src={avatarSrc}
                        alt={displayName}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-2xl font-semibold text-brand">
                        {(displayName || 'U').slice(0, 1).toUpperCase()}
                      </div>
                    )}
                  </button>
                  <button
                    type="button"
                    disabled={isUploadingPhoto || accountId == null}
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
                    aria-label="Change profile photo"
                    title="Change profile photo"
                  >
                    {isUploadingPhoto ? (
                      <span className="text-[10px] font-semibold">…</span>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden
                      >
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                      </svg>
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleProfilePhotoChange}
                  />
                </div>
                <div className="min-w-0 flex-1 text-center sm:text-left">
                  <h2 className="text-lg font-bold text-gray-900 sm:text-xl">{displayName}</h2>
                  <p className="mt-1 text-sm text-gray-600">{displayRole}</p>
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                    <span className="rounded-full bg-[#F7E5D5] px-3 py-1 text-xs font-medium text-gray-700">
                      ID: {profile?.employeeId || employeeId || '—'}
                    </span>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        profile?.status === true
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {formatStatus(profile?.status)}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    {isUploadingPhoto
                      ? 'Uploading photo…'
                      : `Click the edit icon to update photo (max ${IMAGE_MAX_KB}KB).`}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
              <h3 className="mb-4 text-base font-semibold text-brand">Work Information</h3>
              {isFetching && !profile ? (
                <p className="text-sm text-gray-500">Loading profile…</p>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <ProfileField label="Employee ID" value={profile?.employeeId} />
                  <ProfileField label="Accountable Department" value={profile?.department} />
                  <ProfileField
                    label="Designation"
                    value={profile?.roleName || profile?.designation}
                  />
                  <ProfileField label="Location" value={profile?.location} />
                  <ProfileField label="Reporting Supervisor" value={profile?.manager} />
                  <ProfileField label="Date of Joining" value={formatDate(profile?.doj)} />
                  <ProbationInfoFields
                    onProbation={profile?.onProbation}
                    completionDate={profile?.completionDate}
                  />
                </div>
              )}
            </div>

            <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
              <h3 className="mb-4 text-base font-semibold text-brand">Personal Information</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <ProfileField label="Contact Number" value={profile?.contact} />
                <ProfileField label="Date of Birth" value={formatDate(profile?.dob)} />
                <ProfileField label="Email ID" value={profile?.email} wide />
                <ProfileField label="Aadhar Number" value={profile?.aadharNo} />
                <ProfileField label="PAN Number" value={profile?.panNo} />
              </div>
            </div>

            <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
              <h3 className="mb-4 text-base font-semibold text-brand">Documents</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:max-w-2xl">
                {[
                  { label: 'Aadhar', src: profile?.aadharImage },
                  { label: 'PAN', src: profile?.panImage },
                ].map((doc) => {
                  const src = resolveAssetUrl(doc.src) || doc.src;
                  const isPdf = Boolean(src) && isPdfUrl(src);
                  return (
                    <div key={doc.label} className="min-w-0">
                      <p className="mb-1 text-xs font-medium text-gray-500">{doc.label}</p>
                      <div className="aspect-[16/10] overflow-hidden rounded-lg border border-gray-100 bg-gray-100">
                        {!src ? (
                          <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                            {isFetching ? 'Loading…' : 'Not uploaded'}
                          </div>
                        ) : isPdf ? (
                          <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-[#faf8f5] px-3 text-center">
                            <svg
                              width="36"
                              height="36"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.75"
                              className="text-brand"
                              aria-hidden
                            >
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                              <line x1="8" y1="13" x2="16" y2="13" />
                              <line x1="8" y1="17" x2="16" y2="17" />
                              <line x1="8" y1="9" x2="10" y2="9" />
                            </svg>
                            <p className="line-clamp-2 text-xs font-medium text-gray-700">
                              {fileNameFromUrl(src, doc.label)}
                            </p>
                            <a
                              href={src}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="inline-flex items-center justify-center rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white shadow-sm"
                            >
                              View PDF
                            </a>
                          </div>
                        ) : (
                          <img
                            src={src}
                            alt={doc.label}
                            className="h-full w-full cursor-zoom-in object-contain"
                            onClick={() =>
                              setZoomImage({
                                src,
                                title: `${doc.label} - ${displayName}`,
                              })
                            }
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : null}
      </div>

      {zoomImage ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setZoomImage(null)}
          role="presentation"
        >
          <div className="relative max-h-[90vh] max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setZoomImage(null)}
              className="absolute -right-3 -top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white text-xl leading-none text-gray-800 shadow-lg hover:bg-gray-100"
              aria-label="Close preview"
            >
              ×
            </button>
            <img
              src={zoomImage.src}
              alt={zoomImage.title || 'Preview'}
              className="max-h-[90vh] w-full rounded-lg object-contain"
            />
            {zoomImage.title ? (
              <p className="mt-2 text-center text-sm font-medium text-white">{zoomImage.title}</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </DashboardLayout>
  );
}

export default UserProfilePage;
